import React from 'react'
import AgentChat from './AgentChat'

function PropertyInput({ 
  property,
  // Chat-related props
  chatMessages,
  isChatConnected,
  isChatConnecting,
  isChatStreaming,
  chatError,
  onSendChatMessage,
  onReconnectChat,
  analysisResult,
  isAnalysisComplete
}) {
  const formatCurrency = (value) => {
    if (value == null || value === '') return 'N/A'
    const n = Number(value)
    if (Number.isNaN(n)) return 'N/A'
    return `£${n.toLocaleString()}`
  }

  if (!property) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Property Details</h3>
        
        {/* Address */}
        <div className="mb-6">
          <div className="text-sm text-gray-500 mb-1">Address</div>
          <div className="font-semibold text-gray-900">
            {property.address.line_1}
          </div>
          {property.address.line_2 && (
            <div className="text-gray-700">{property.address.line_2}</div>
          )}
          <div className="text-gray-700">
            {property.address.city}, {property.address.postcode}
          </div>
        </div>

        {/* Price */}
        <div className="mb-6 p-4 bg-primary-50 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">Asking Price</div>
          <div className="text-2xl font-bold text-primary-700">
            {formatCurrency(property.financials.asking_price)}
          </div>
        </div>

        {/* Key Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <div className="text-sm text-gray-500">Type</div>
            <div className="font-semibold capitalize">
              {property.features.property_type}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Tenure</div>
            <div className="font-semibold capitalize">
              {property.features.tenure}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Bedrooms</div>
            <div className="font-semibold">{property.features.bedrooms}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Bathrooms</div>
            <div className="font-semibold">{property.features.bathrooms}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Size</div>
            <div className="font-semibold">
              {property.features.sqft ? `${property.features.sqft} sqft` : 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">EPC Rating</div>
            <div className="font-semibold">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                property.features.epc_rating === 'A' || property.features.epc_rating === 'B' 
                  ? 'bg-green-100 text-green-800' :
                property.features.epc_rating === 'C' || property.features.epc_rating === 'D'
                  ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {property.features.epc_rating || 'N/A'}
              </span>
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Year Built</div>
            <div className="font-semibold">
              {property.features.year_built || 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">£/sqft</div>
            <div className="font-semibold">
              {property.financials.price_per_sqft 
                ? `£${Math.round(property.financials.price_per_sqft).toLocaleString()}`
                : 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Council Tax</div>
            <div className="font-semibold">
              {property.financials.council_tax_band ? (
                <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-800">
                  Band {property.financials.council_tax_band}
                </span>
              ) : 'N/A'}
            </div>
          </div>
        </div>

        {/* Financials */}
        <div className="border-t border-gray-200 pt-4 mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Financials</h4>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Monthly Rent (Est)</span>
              <span className="font-semibold">
                {formatCurrency(property.financials.monthly_rent)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Gross Yield</span>
              <span className={`font-semibold ${
                property.financials.gross_yield != null
                  ? (property.financials.gross_yield >= 6 ? 'text-green-600' : property.financials.gross_yield >= 5 ? 'text-yellow-600' : 'text-orange-600')
                  : 'text-gray-500'
              }`}>
                {property.financials.gross_yield != null ? `${property.financials.gross_yield}%` : 'N/A'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Net Yield</span>
              <span className="font-semibold text-gray-900">
                {property.financials.net_yield != null ? `${property.financials.net_yield}%` : 'N/A'}
              </span>
            </div>
            
            {property.financials.service_charge > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Service Charge</span>
                <span className="font-semibold">
                  {formatCurrency(property.financials.service_charge)}/mo
                </span>
              </div>
            )}
            
            {property.financials.ground_rent > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Ground Rent</span>
                <span className="font-semibold">
                  {formatCurrency(property.financials.ground_rent)}/yr
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="border-t border-gray-200 pt-4 mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Features & Location</h4>
          <div className="flex flex-wrap gap-2 mb-3">
            {property.features.has_parking && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                ✓ Parking
              </span>
            )}
            {property.features.has_garden && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                ✓ Garden
              </span>
            )}
            {property.location?.nearest_station && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                🚇 {property.location.station_distance_miles}mi to {property.location.nearest_station}
              </span>
            )}
            {property.location?.nearest_school && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                🎓 {property.location.school_distance_miles != null ? `${property.location.school_distance_miles}mi to ` : ''}{property.location.nearest_school}
              </span>
            )}
          </div>
          
          {/* Data Source & Last Updated */}
          <div className="flex items-center justify-between text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
            <span>Source: {property.data_source || 'unknown'}</span>
            {property.last_updated && (
              <span>Updated: {new Date(property.last_updated).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        {/* Description */}
        {property.description && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Description</h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              {property.description}
            </p>
          </div>
        )}
      </div>
      
      {/* Agent Chat - Fixed at Bottom */}
      <AgentChat
        messages={chatMessages || []}
        isConnected={isChatConnected}
        isConnecting={isChatConnecting}
        isStreaming={isChatStreaming}
        error={chatError}
        onSendMessage={onSendChatMessage}
        onReconnect={onReconnectChat}
        analysisComplete={isAnalysisComplete}
      />
    </div>
  )
}

export default PropertyInput
